import React, { useState, useRef } from 'react';
import { useShop } from '../../context/ShopContext';
import { api } from '../../services/api';
import { formatINR } from '../../data/products';
import { 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  X, 
  Upload, 
  Image as ImageIcon, 
  Sparkles, 
  Camera,
  Truck,
  Clock,
  Video,
  Film,
  PlayCircle,
  RotateCcw
} from 'lucide-react';

export default function AdminProducts() {
  const { 
    products, 
    categories,
    addProduct, 
    updateProduct, 
    deleteProduct, 
    toggleProductStock,
    masterMetalRates,
    getMasterRateForProduct,
    calculateProductPrice,
    storeSettings,
    restoreMasterCatalog,
    addToast
  } = useShop();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCat, setSelectedCat] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const videoInputRef = useRef(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  const initialMainImage = 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80';
  const initialSubImages = ['', '', '', '', ''];

  const initialForm = {
    name: '',
    subtitle: '',
    category: categories[0]?.id || '',
    metalType: 'Gold',
    purity: '22KT (916)',
    weight: '10.000',
    makingChargePercent: '12',
    pricingMode: 'auto',
    price: '',
    customPrice: '',
    originalPrice: '',
    size: '7',
    sizesStr: '6, 7, 8, 9, 10, 12',
    availability: 'In Stock (Dispatch in 24-48 Hours)',
    delivery: '3-5 Days (Insured Nationwide Transit)',
    description: '',
    inStock: true,
    mainImage: initialMainImage,
    subImages: initialSubImages,
    videoUrl: ''
  };

  const [form, setForm] = useState(initialForm);

  const openAddModal = () => {
    setEditingId(null);
    const defaultPurity = masterMetalRates?.gold?.[1]?.label || masterMetalRates?.gold?.[0]?.label || '22KT (916)';
    setForm({
      ...initialForm,
      metalType: 'Gold',
      purity: defaultPurity,
      weight: '10.000',
      makingChargePercent: '12',
      pricingMode: 'auto',
      price: '',
      customPrice: '',
      originalPrice: '',
      availability: storeSettings?.defaultAvailability || 'In Stock (Dispatch in 24-48 Hours)',
      delivery: storeSettings?.defaultDelivery || '3-5 Days (Insured Nationwide Transit)',
      videoUrl: ''
    });
    setModalOpen(true);
  };

  const openEditModal = (prod) => {
    setEditingId(prod.id);
    const existingImages = prod.images || [];
    const mainImg = existingImages[0] || initialMainImage;
    const subs = [
      existingImages[1] || '',
      existingImages[2] || '',
      existingImages[3] || '',
      existingImages[4] || '',
      existingImages[5] || ''
    ];

    const isSilverItem = 
      prod.metalType === 'Silver' || 
      prod.category === 'silverware' ||
      (typeof prod.metal === 'string' && prod.metal.toLowerCase().includes('silver')) ||
      (typeof prod.purity === 'string' && prod.purity.toLowerCase().includes('silver'));

    const detectedMetal = prod.metalType || (isSilverItem ? 'Silver' : (prod.category === 'kitchenware' ? 'Kitchenware' : 'Gold'));

    let detectedPurity = prod.purity || prod.goldPurity || '';
    if (!detectedPurity) {
      detectedPurity = detectedMetal === 'Silver' ? '999 Pure Silver' : '22KT (916)';
    }

    let detectedWeight = prod.weight;
    if (detectedWeight === undefined || detectedWeight === null || detectedWeight === '') {
      detectedWeight = parseFloat(prod.goldWeight || prod.netGoldWeight || prod.grossWeight || 10) || 10;
    }

    let detectedMaking = prod.makingChargePercent;
    if (detectedMaking === undefined || detectedMaking === null || detectedMaking === '') {
      detectedMaking = parseFloat(prod.priceBreakup?.makingChargePercent || prod.priceBreakup?.makingDiscountPercent || 12) || 12;
    }

    const detectedPricingMode = prod.pricingMode || prod.pricing_mode || (
      prod.category === 'kitchenware' && !isSilverItem ? 'manual' : 'auto'
    );

    const existingPrice = prod.price || '';
    const existingOriginalPrice = prod.originalPrice || '';

    setForm({
      ...prod,
      name: prod.name || '',
      subtitle: prod.subtitle || '',
      category: prod.category || categories[0]?.id || '',
      metalType: detectedMetal,
      purity: detectedPurity,
      weight: detectedWeight,
      makingChargePercent: detectedMaking,
      pricingMode: detectedPricingMode,
      price: existingPrice,
      customPrice: existingPrice,
      originalPrice: existingOriginalPrice,
      size: prod.size || '7',
      sizesStr: Array.isArray(prod.sizes) ? prod.sizes.join(', ') : (prod.sizes || '6, 7, 8, 9, 10, 12'),
      availability: prod.availability || storeSettings?.defaultAvailability || 'In Stock (Dispatch in 24-48 Hours)',
      delivery: prod.delivery || storeSettings?.defaultDelivery || '3-5 Days (Insured Nationwide Transit)',
      mainImage: mainImg,
      subImages: subs,
      videoUrl: prod.videoUrl || prod.video_url || prod.video || '',
      inStock: prod.inStock !== false
    });
    setModalOpen(true);
  };

  // =========================================================
  // LIVE PRICE CALCULATION (SUPPORTS AUTO BULLION & MANUAL OVERRIDE)
  // =========================================================
  const currentRatePerGram = getMasterRateForProduct(
    { metalType: form.metalType, purity: form.purity, category: form.category },
    masterMetalRates
  );
  const currentWeight = parseFloat(form.weight) || 0;
  const currentMakingPct = parseFloat(form.makingChargePercent) || 0;
  const currentMetalValue = Math.round(currentWeight * currentRatePerGram);
  const currentMakingCharge = Math.round(currentMetalValue * (currentMakingPct / 100));
  const currentTaxable = currentMetalValue + currentMakingCharge;
  const currentGst = Math.round(currentTaxable * 0.03);
  const autoCalculatedPrice = currentTaxable + currentGst;

  const isManualPricing = form.pricingMode === 'manual';
  const customPriceNum = parseFloat(form.customPrice !== undefined ? form.customPrice : form.price) || 0;
  const currentFinalPrice = (isManualPricing && customPriceNum > 0) ? customPriceNum : autoCalculatedPrice;

  // Client-side image optimization (resizes heavy phone camera shots to lightweight ~150KB)
  const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.85) => {
    return new Promise((resolve) => {
      if (!file || !file.type?.startsWith('image/') || file.type === 'image/svg+xml') {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => resolve(e.target.result);
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e, type, index = 0) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      addToast('Optimizing jewellery photo...', 'info');
      const optimizedBase64 = await compressImage(file, 1200, 1200, 0.85);

      // Try uploading to server to store as file URL, or fallback to optimized base64
      let finalImageUrl = optimizedBase64;
      try {
        const uploadedUrl = await api.uploadImage(optimizedBase64);
        if (uploadedUrl) finalImageUrl = uploadedUrl;
      } catch (uploadErr) {
        console.warn('Direct upload skipped, using optimized inline data:', uploadErr);
      }

      if (type === 'main') {
        setForm((prev) => ({ ...prev, mainImage: finalImageUrl }));
        addToast('Main Cover Image updated & optimized!', 'success');
      } else {
        setForm((prev) => {
          const updatedSubs = [...prev.subImages];
          updatedSubs[index] = finalImageUrl;
          return { ...prev, subImages: updatedSubs };
        });
        addToast(`Sub Image ${index + 1} updated!`, 'success');
      }
    } catch (err) {
      console.error('Error processing image:', err);
      addToast('Failed to process image', 'error');
    }
  };

  const removeSubImage = (index) => {
    setForm((prev) => {
      const updatedSubs = [...prev.subImages];
      updatedSubs[index] = '';
      return { ...prev, subImages: updatedSubs };
    });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 120 * 1024 * 1024) {
      addToast('Video file size 120MB se jyada hai. Please chhota clip chunein ya compress karein.', 'error');
      return;
    }

    setIsUploadingVideo(true);
    addToast('Jewellery reel video upload ho raha hai...', 'info');

    try {
      const uploadedUrl = await api.uploadVideo(file);
      if (uploadedUrl) {
        setForm((prev) => ({ ...prev, videoUrl: uploadedUrl }));
        addToast('Video successfully upload ho gayi!', 'success');
        setIsUploadingVideo(false);
        return;
      }
    } catch (err) {
      console.warn('Direct upload skipped, attempting inline read:', err);
    }

    // Fallback: Read as base64 DataURL if under 35MB
    if (file.size <= 35 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result;
        if (dataUrl) {
          setForm((prev) => ({ ...prev, videoUrl: dataUrl }));
          addToast('Video reel successfully attach ho gayi!', 'success');
        }
        setIsUploadingVideo(false);
      };
      reader.onerror = () => {
        addToast('Video file read karne me dikkat aayi.', 'error');
        setIsUploadingVideo(false);
      };
      reader.readAsDataURL(file);
    } else {
      addToast('Video size 35MB se badi hai. Please server running rakhein ya YouTube link dalein.', 'error');
      setIsUploadingVideo(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!form.mainImage) {
      addToast('Please choose at least 1 Main Cover Image', 'error');
      return;
    }

    const combinedImages = [
      form.mainImage,
      ...form.subImages.filter((img) => Boolean(img && img.trim()))
    ];

    const sizesArray = form.sizesStr 
      ? form.sizesStr.split(',').map(s => s.trim()).filter(Boolean)
      : (form.sizes || ['7']);

    const weightNum = parseFloat(form.weight) || 0;
    const makingPct = parseFloat(form.makingChargePercent) || 0;
    const metalVal = Math.round(weightNum * currentRatePerGram);
    const makingVal = Math.round(metalVal * (makingPct / 100));
    const diamondVal = parseFloat(form.diamondAmount || form.priceBreakup?.diamondAmount) || 0;
    const gemstoneVal = parseFloat(form.gemstoneAmount || form.priceBreakup?.gemstoneAmount) || 0;

    const isManual = form.pricingMode === 'manual';
    const manualVal = parseFloat(form.customPrice !== undefined ? form.customPrice : form.price) || 0;
    const currentTaxable = metalVal + makingVal + diamondVal + gemstoneVal;
    const currentGst = Math.round(currentTaxable * 0.03);
    const finalPrice = (isManual && manualVal > 0) ? Math.round(manualVal) : (currentTaxable + currentGst);
    const finalOriginalPrice = form.originalPrice && parseFloat(form.originalPrice) > 0 
      ? Math.round(parseFloat(form.originalPrice)) 
      : Math.round(finalPrice * 1.08);

    const productPayload = {
      ...form,
      sizes: sizesArray,
      images: combinedImages,
      pricingMode: isManual ? 'manual' : 'auto',
      metalType: form.metalType,
      purity: form.purity,
      availability: form.availability ? form.availability.trim() : 'In Stock (Dispatch in 24-48 Hours)',
      delivery: form.delivery ? form.delivery.trim() : '3-5 Days (Insured Nationwide Transit)',
      videoUrl: form.videoUrl ? form.videoUrl.trim() : '',
      weight: weightNum,
      makingChargePercent: makingPct,
      metal: `${form.purity} ${form.metalType}`,
      goldPurity: form.purity,
      grossWeight: weightNum > 0 ? `${weightNum} G` : (form.grossWeight || ''),
      netGoldWeight: weightNum > 0 ? `${weightNum} G` : (form.netGoldWeight || ''),
      price: finalPrice,
      diamondAmount: diamondVal,
      gemstoneAmount: gemstoneVal,
      originalPrice: finalOriginalPrice,
      priceBreakup: {
        ...(form.priceBreakup || {}),
        metalValue: metalVal,
        goldAmount: metalVal,
        diamondAmount: diamondVal,
        gemstoneAmount: gemstoneVal,
        makingCharge: makingVal,
        makingChargesDiscounted: makingVal,
        makingChargePercent: makingPct,
        ratePerGram: currentRatePerGram,
        weight: weightNum,
        taxableAmount: currentTaxable,
        gstAmount: currentGst,
        finalTotal: finalPrice
      }
    };

    if (editingId) {
      updateProduct(editingId, productPayload);
      addToast(`Product updated! Price: ${formatINR(finalPrice)} (${isManual ? 'Custom' : 'Auto'})`, 'success');
    } else {
      addProduct({
        ...productPayload,
        id: `ml-custom-${Date.now()}`
      });
      addToast(`Product published! Price: ${formatINR(finalPrice)} (${isManual ? 'Custom' : 'Auto'})`, 'success');
    }
    setModalOpen(false);
  };

  const filtered = products.filter((p) => {
    const matchesSearch = 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (p.metal && p.metal.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.purity && p.purity.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCat = selectedCat === 'all' || p.category === selectedCat;
    return matchesSearch && matchesCat;
  });

  return (
    <div>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff' }}>
            Jewellery <span className="text-gold-gradient">Inventory & Vault</span>
          </h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            All prices are automatically computed from Master Bullion Rates. No manual price entry.
          </p>
        </div>

        <button className="btn btn-gold btn-sm" onClick={openAddModal}>
          <Plus size={16} /> Add New Jewellery Item
        </button>
      </div>

      {/* Filter Row */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: '#0d0d16',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
          padding: '0.45rem 0.8rem',
          flex: 1,
          minWidth: '220px'
        }}>
          <Search size={15} color="var(--gold-400)" style={{ marginRight: '0.5rem' }} />
          <input
            type="text"
            placeholder="Search catalog by name, purity, metal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: '0.84rem',
              width: '100%'
            }}
          />
        </div>

        {/* Category selector */}
        <select
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          style={{
            background: '#0d0d16',
            border: '1px solid var(--border-subtle)',
            color: '#fff',
            padding: '0.5rem 0.8rem',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.84rem',
            outline: 'none',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Categories ({products.length})</option>
          {categories.map((cat) => {
            const count = products.filter((p) => p.category === cat.id).length;
            return (
              <option key={cat.id} value={cat.id}>
                {cat.name} ({count})
              </option>
            );
          })}
        </select>
      </div>

      {/* Products Table */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.84rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.72rem', background: '#0e0e16' }}>
                <th style={{ padding: '0.85rem 0.75rem' }}>Jewellery & Images</th>
                <th style={{ padding: '0.85rem 0.75rem' }}>Metal & Purity</th>
                <th style={{ padding: '0.85rem 0.75rem' }}>Weight</th>
                <th style={{ padding: '0.85rem 0.75rem' }}>Making %</th>
                <th style={{ padding: '0.85rem 0.75rem' }}>Auto Calculated Price</th>
                <th style={{ padding: '0.85rem 0.75rem' }}>Stock</th>
                <th style={{ padding: '0.85rem 0.75rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((prod) => {
                const livePricing = calculateProductPrice(prod, masterMetalRates);
                const displayWeight = prod.weight || parseFloat(prod.goldWeight || prod.netGoldWeight || prod.grossWeight || 1.0);
                const displayMaking = prod.makingChargePercent || (prod.priceBreakup?.makingChargePercent || prod.priceBreakup?.makingDiscountPercent ? parseFloat(prod.priceBreakup.makingChargePercent || prod.priceBreakup.makingDiscountPercent) : 12);
                const displayPurity = prod.purity || prod.goldPurity || '22KT (916)';
                const displayMetal = prod.metalType || (prod.category === 'kitchenware' ? 'Kitchenware' : 'Gold');

                return (
                  <tr key={prod.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    {/* Photo & Name */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <div style={{ position: 'relative' }}>
                          <img
                            src={prod.images?.[0]}
                            alt={prod.name}
                            style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-sm)', objectFit: 'cover' }}
                          />
                          <span style={{
                            position: 'absolute',
                            bottom: '-4px',
                            right: '-4px',
                            background: 'rgba(212, 175, 55, 0.95)',
                            color: '#0a0a0f',
                            fontSize: '0.62rem',
                            fontWeight: 800,
                            padding: '0.1rem 0.35rem',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                          }}>
                            {prod.images?.length || 1} imgs
                          </span>
                        </div>
                        <div>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {prod.name}
                            {(prod.videoUrl || prod.video_url || prod.video) && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: '#f87171',
                                border: '1px solid rgba(239, 68, 68, 0.35)',
                                borderRadius: '4px',
                                padding: '1px 5px',
                                fontSize: '0.65rem',
                                fontWeight: 700
                              }}>
                                <Video size={10} /> Video
                              </span>
                            )}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{prod.subtitle}</div>
                        </div>
                      </div>
                    </td>

                    {/* Metal & Purity */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <div style={{ color: displayMetal === 'Silver' ? '#cbd5e1' : 'var(--gold-400)', fontWeight: 700 }}>
                        {displayMetal}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.74rem' }}>
                        {displayPurity}
                      </div>
                    </td>

                    {/* Weight */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <div style={{ fontWeight: 700, color: '#fff' }}>
                        {displayWeight} g
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        @ ₹{livePricing.ratePerGram}/g
                      </div>
                    </td>

                    {/* Making Charge % */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <span style={{
                        background: 'rgba(212, 175, 55, 0.12)',
                        color: 'var(--gold-300)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.76rem',
                        fontWeight: 700
                      }}>
                        {displayMaking}%
                      </span>
                    </td>

                    {/* Calculated / Custom Price */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <div style={{ fontWeight: 800, color: '#fff', fontFamily: 'var(--font-serif)', fontSize: '1.02rem' }}>
                        {formatINR(livePricing.finalPrice)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                        {prod.pricingMode === 'manual' || prod.pricing_mode === 'manual' ? (
                          <span style={{ color: '#60a5fa', fontWeight: 700, background: 'rgba(59, 130, 246, 0.1)', padding: '1px 5px', borderRadius: '3px' }}>
                            Fixed Custom Price
                          </span>
                        ) : (
                          `Metal: ${formatINR(livePricing.metalValue)} + Making: ${formatINR(livePricing.makingCharge)} + GST (3%): ${formatINR(livePricing.gstAmount)}`
                        )}
                      </div>
                    </td>

                    {/* Stock Toggle */}
                    <td style={{ padding: '0.8rem 0.75rem' }}>
                      <button
                        onClick={() => toggleProductStock(prod.id)}
                        style={{
                          background: prod.inStock ? 'rgba(52, 211, 153, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                          color: prod.inStock ? '#34d399' : '#f87171',
                          border: `1px solid ${prod.inStock ? 'rgba(52, 211, 153, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                          borderRadius: '4px',
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {prod.inStock ? 'In Stock' : 'Out of Stock'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '0.8rem 0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                        <button
                          onClick={() => openEditModal(prod)}
                          className="btn-icon"
                          title="Edit Product Details"
                          style={{ width: '30px', height: '30px' }}
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove "${prod.name}" from catalog?`)) {
                              deleteProduct(prod.id);
                            }
                          }}
                          className="btn-icon"
                          title="Delete Product"
                          style={{ width: '30px', height: '30px', color: '#f87171' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* =========================================================
          ADD / EDIT PRODUCT MODAL
          ========================================================= */}
      {modalOpen && (
        <div 
          className="modal-overlay active"
          onClick={() => setModalOpen(false)}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '820px', padding: '2rem' }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#fff' }}>
                  {editingId ? 'Edit Jewellery Item' : 'Add New Jewellery to Vault'}
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Final Price is automatically calculated from Master Bullion Rates.
                </p>
              </div>
              <button 
                onClick={() => setModalOpen(false)}
                className="btn-icon"
                style={{ width: '34px', height: '34px' }}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
              
              {/* Basic Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 700 }}>
                    Jewellery Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-luxury"
                    placeholder="e.g. Royal Nizam Emerald & Polki Choker"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 700 }}>
                    Category *
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="input-luxury"
                  >
                    {categories.length === 0 ? (
                      <option value="">No categories created yet</option>
                    ) : (
                      categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 700 }}>
                  Subtitle / Craft Description
                </label>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  className="input-luxury"
                  placeholder="e.g. Handcrafted with traditional royal heritage finish"
                />
              </div>

              {/* =========================================================
                  DYNAMIC & MANUAL PRICING CONFIGURATION
                  ========================================================= */}
              <div style={{
                background: '#13131e',
                border: '1px solid var(--border-gold)',
                borderRadius: 'var(--radius-md)',
                padding: '1.4rem'
              }}>
                {/* Mode Selector Tabs */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginBottom: '1.2rem', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold-400)' }}>
                    PRICING METHOD:
                  </div>
                  <div style={{ display: 'inline-flex', background: 'rgba(0, 0, 0, 0.4)', padding: '3px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, pricingMode: 'auto' }))}
                      style={{
                        padding: '0.4rem 0.9rem',
                        borderRadius: '6px',
                        fontSize: '0.76rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: form.pricingMode !== 'manual' ? 'var(--gold-500)' : 'transparent',
                        color: form.pricingMode !== 'manual' ? '#000' : 'var(--text-muted)',
                        border: 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      ⚡ Auto Live Bullion
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        pricingMode: 'manual',
                        customPrice: prev.customPrice || prev.price || currentFinalPrice
                      }))}
                      style={{
                        padding: '0.4rem 0.9rem',
                        borderRadius: '6px',
                        fontSize: '0.76rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: form.pricingMode === 'manual' ? 'var(--gold-500)' : 'transparent',
                        color: form.pricingMode === 'manual' ? '#000' : 'var(--text-muted)',
                        border: 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      🏷️ Custom / Fixed Price
                    </button>
                  </div>
                </div>

                {/* Common Metal & Purity Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.2rem'
                }}>
                  {/* Metal */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                      Metal / Category Type
                    </label>
                    <select
                      value={form.metalType}
                      onChange={(e) => {
                        const newMetal = e.target.value;
                        let defaultPurity = '22KT (916)';
                        if (newMetal === 'Kitchenware') {
                          defaultPurity = 'SS-304 Food Grade';
                        } else if (newMetal === 'Silver') {
                          defaultPurity = masterMetalRates?.silver?.[0]?.label || '999 Pure Silver';
                        } else {
                          defaultPurity = masterMetalRates?.gold?.[1]?.label || masterMetalRates?.gold?.[0]?.label || '22KT (916)';
                        }
                        setForm({ ...form, metalType: newMetal, purity: defaultPurity });
                      }}
                      className="input-luxury"
                      style={{ fontWeight: 700 }}
                    >
                      <option value="Gold">Gold Jewellery</option>
                      <option value="Silver">Silver Jewellery & Silverware</option>
                      <option value="Kitchenware">Heritage Kitchenware / Cookware</option>
                    </select>
                  </div>

                  {/* Carat / Purity */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                      Carat / Purity
                    </label>
                    <select
                      value={form.purity}
                      onChange={(e) => setForm({ ...form, purity: e.target.value })}
                      className="input-luxury"
                      style={{ fontWeight: 700 }}
                    >
                      {form.metalType === 'Kitchenware' ? (
                        [
                          { id: 'ss-304', label: 'SS-304 Food Grade' },
                          { id: 'copper-brass', label: 'Pure Copper & Brass' },
                          { id: 'kalai', label: 'Kalai Coated' }
                        ].map((m) => (
                          <option key={m.id} value={m.label}>{m.label}</option>
                        ))
                      ) : form.metalType === 'Silver' ? (
                        (masterMetalRates?.silver || []).map((m) => (
                          <option key={m.id} value={m.label}>
                            {m.label} (₹{m.rate}/g)
                          </option>
                        ))
                      ) : (
                        (masterMetalRates?.gold || []).map((m) => (
                          <option key={m.id} value={m.label}>
                            {m.label} (₹{m.rate}/g)
                          </option>
                        ))
                      )}
                      <option value="__CUSTOM__">+ Custom Purity / Type...</option>
                    </select>

                    {/* Custom Purity Input if __CUSTOM__ or non-standard purity is active */}
                    {(form.purity === '__CUSTOM__' || (
                      form.metalType === 'Silver' && 
                      !(masterMetalRates?.silver || []).some(m => m.label === form.purity)
                    ) || (
                      form.metalType === 'Gold' && 
                      !(masterMetalRates?.gold || []).some(m => m.label === form.purity)
                    )) && (
                      <input
                        type="text"
                        placeholder="Enter custom purity (e.g. 800 Silver, 70% Silver, 20K)"
                        value={form.purity === '__CUSTOM__' ? '' : form.purity}
                        onChange={(e) => setForm({ ...form, purity: e.target.value })}
                        style={{
                          marginTop: '6px',
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          border: '1px solid #f59e0b',
                          background: '#fffbeb',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: '#b45309'
                        }}
                      />
                    )}
                  </div>

                  {/* Weight (Gram) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                      Weight (Gram)
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      required={form.pricingMode !== 'manual'}
                      placeholder="10.000"
                      value={form.weight}
                      onChange={(e) => setForm({ ...form, weight: e.target.value })}
                      className="input-luxury"
                      style={{ fontWeight: 700 }}
                    />
                  </div>

                  {/* Making Charge (%) */}
                  {form.pricingMode !== 'manual' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                        Making Charge (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        required
                        placeholder="12"
                        value={form.makingChargePercent}
                        onChange={(e) => setForm({ ...form, makingChargePercent: e.target.value })}
                        className="input-luxury"
                        style={{ fontWeight: 700 }}
                      />
                    </div>
                  )}
                </div>

                {/* Manual Price Inputs */}
                {form.pricingMode === 'manual' ? (
                  <div style={{
                    background: 'rgba(212, 175, 55, 0.05)',
                    border: '1px solid rgba(212, 175, 55, 0.25)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-300)', marginBottom: '0.35rem', fontWeight: 700 }}>
                          Selling Price (₹) *
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          required
                          placeholder="e.g. 45000"
                          value={form.customPrice !== undefined ? form.customPrice : (form.price || '')}
                          onChange={(e) => setForm({ ...form, customPrice: e.target.value, price: e.target.value })}
                          className="input-luxury"
                          style={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.35rem', fontWeight: 700 }}>
                          Strikethrough Price (₹ MRP, optional)
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          placeholder="e.g. 52000"
                          value={form.originalPrice || ''}
                          onChange={(e) => setForm({ ...form, originalPrice: e.target.value })}
                          className="input-luxury"
                          style={{ fontWeight: 600 }}
                        />
                      </div>
                    </div>
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>
                      💡 This price will remain fixed and will <strong>NOT</strong> change automatically with gold rate fluctuations.
                    </p>
                  </div>
                ) : (
                  /* Auto Calculation Breakdown */
                  <div style={{
                    borderTop: '1px dashed rgba(212, 175, 55, 0.4)',
                    paddingTop: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    marginBottom: '0.8rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      <span>Master Bullion Rate:</span>
                      <strong style={{ color: '#fff' }}>₹{currentRatePerGram.toLocaleString('en-IN')}/g</strong>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      <span>Metal Value ({currentWeight}g × ₹{currentRatePerGram}/g):</span>
                      <strong style={{ color: '#34d399' }}>{formatINR(currentMetalValue)}</strong>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      <span>Making Charge ({currentMakingPct}%):</span>
                      <strong style={{ color: '#fbbf24' }}>{formatINR(currentMakingCharge)}</strong>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      <span>Applicable GST (3%):</span>
                      <strong style={{ color: '#38bdf8' }}>+ {formatINR(currentGst)}</strong>
                    </div>
                  </div>
                )}

                {/* Final Price Summary Box */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(212, 175, 55, 0.3)',
                  paddingTop: '0.9rem',
                  marginTop: '0.4rem'
                }}>
                  <div>
                    <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', letterSpacing: '0.04em' }}>
                      FINAL SELLING PRICE:
                    </span>
                    <span style={{
                      marginLeft: '0.6rem',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: form.pricingMode === 'manual' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(52, 211, 153, 0.2)',
                      color: form.pricingMode === 'manual' ? '#60a5fa' : '#34d399'
                    }}>
                      {form.pricingMode === 'manual' ? 'CUSTOM FIXED' : 'AUTO LIVE'}
                    </span>
                  </div>
                  <span style={{ fontSize: '1.45rem', fontWeight: 900, color: 'var(--gold-300)', fontFamily: 'var(--font-serif)' }}>
                    {formatINR(currentFinalPrice)}
                  </span>
                </div>
              </div>

              {/* Sizes / Dimensions (Optional) */}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem', fontWeight: 700 }}>
                  Available Sizes / Dimensions (Optional, comma-separated)
                </label>
                <input
                  type="text"
                  value={form.sizesStr}
                  onChange={(e) => setForm({ ...form, sizesStr: e.target.value })}
                  className="input-luxury"
                  placeholder="e.g. 6, 7, 8, 9, 10, 12, Standard"
                />
              </div>

              {/* =========================================================
                  AVAILABILITY & DELIVERY FULFILLMENT PLAN
                  ========================================================= */}
              <div style={{
                background: '#13131e',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                borderRadius: 'var(--radius-md)',
                padding: '1.25rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', paddingBottom: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Truck size={17} color="var(--gold-400)" />
                    <h4 style={{ fontSize: '0.92rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                      Availability & Delivery Fulfillment Plan
                    </h4>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    Visible directly to buyers on storefront
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem' }}>
                  {/* Availability / Dispatch Status */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#34d399', marginBottom: '0.35rem', fontWeight: 700 }}>
                      ● Availability / Dispatch Status
                    </label>
                    <input
                      type="text"
                      value={form.availability}
                      onChange={(e) => setForm({ ...form, availability: e.target.value })}
                      className="input-luxury"
                      placeholder="e.g. In Stock (Dispatch in 24-48 Hours)"
                      style={{ fontWeight: 600, color: '#34d399' }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                      {[
                        'In Stock (Dispatch in 24-48 Hours)',
                        'Made to Order (7-10 Days)',
                        'Ready in Showroom (Instant Pickup)',
                        'Handcrafted on Demand (12-15 Days)'
                      ].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setForm({ ...form, availability: preset })}
                          style={{
                            fontSize: '0.68rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: form.availability === preset ? 'rgba(52, 211, 153, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            color: form.availability === preset ? '#34d399' : 'var(--text-muted)',
                            border: form.availability === preset ? '1px solid #34d399' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Delivery / Transit Timeline */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-300)', marginBottom: '0.35rem', fontWeight: 700 }}>
                      🚚 Delivery / Transit Timeline
                    </label>
                    <input
                      type="text"
                      value={form.delivery}
                      onChange={(e) => setForm({ ...form, delivery: e.target.value })}
                      className="input-luxury"
                      placeholder="e.g. 3-5 Days (Insured Nationwide Transit)"
                      style={{ fontWeight: 600, color: '#fff' }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                      {[
                        '3-5 Days (Insured Nationwide Transit)',
                        '2-4 Days Express Air Courier',
                        'Same Day Local Courier',
                        '5-7 Days Standard Insured Transit'
                      ].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setForm({ ...form, delivery: preset })}
                          style={{
                            fontSize: '0.68rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: form.delivery === preset ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            color: form.delivery === preset ? 'var(--gold-300)' : 'var(--text-muted)',
                            border: form.delivery === preset ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Images (1 Main + 5 Sub Images) */}
              <div style={{
                background: '#0d0d16',
                border: '1px solid var(--border-gold)',
                borderRadius: 'var(--radius-md)',
                padding: '1.4rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                  <Camera size={18} color="var(--gold-400)" />
                  <div>
                    <h4 style={{ fontSize: '1rem', color: '#fff', fontWeight: 700 }}>
                      Product Images (1 Main Cover + 5 Sub Angle Images)
                    </h4>
                    <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                      Upload 1 primary cover image and optional detail views.
                    </p>
                  </div>
                </div>

                {/* 1. Main Cover Image */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: 'var(--gold-400)',
                    marginBottom: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}>
                    ★ 1. Main Cover Image (Primary Display Image) *
                  </label>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '1.2rem',
                    background: '#13131e',
                    border: '1px dashed var(--border-gold)',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem'
                  }}>
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      backgroundColor: '#0a0a0f',
                      border: '1px solid var(--border-gold)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {form.mainImage ? (
                        <img 
                          src={form.mainImage} 
                          alt="Main Preview" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      ) : (
                        <ImageIcon size={32} color="var(--text-muted)" />
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: '220px' }}>
                      <div style={{ fontSize: '0.84rem', color: '#fff', fontWeight: 600, marginBottom: '0.3rem' }}>
                        {form.mainImage ? 'Main Cover Image Selected' : 'No Main Image Chosen'}
                      </div>
                      <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '0.7rem' }}>
                        This is the primary showcase image displayed on product cards and catalog.
                      </p>

                      <label 
                        className="btn btn-gold btn-sm"
                        style={{ cursor: 'pointer', display: 'inline-flex', gap: '0.4rem', fontSize: '0.76rem' }}
                      >
                        <Upload size={14} />
                        <span>Choose Main Image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'main')}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* 2. Sub Images */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                    <label style={{
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: '#fff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em'
                    }}>
                      2. Sub Images (5 Angles / Detail Views)
                    </label>
                    <span style={{ fontSize: '0.72rem', color: 'var(--gold-400)' }}>
                      {form.subImages.filter(Boolean).length} of 5 Sub Images uploaded
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '0.85rem'
                  }}>
                    {form.subImages.map((subImg, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: '#13131e',
                          border: subImg ? '1px solid var(--border-gold)' : '1px dashed var(--border-subtle)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '0.6rem',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          backgroundColor: '#09090d',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}>
                          {subImg ? (
                            <>
                              <img
                                src={subImg}
                                alt={`Sub Image ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                onClick={() => removeSubImage(idx)}
                                style={{
                                  position: 'absolute',
                                  top: '2px',
                                  right: '2px',
                                  background: 'rgba(239, 68, 68, 0.9)',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer'
                                }}
                                title="Remove this image"
                              >
                                <X size={12} />
                              </button>
                            </>
                          ) : (
                            <Upload size={20} color="var(--text-muted)" />
                          )}
                        </div>

                        <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                          Sub Image {idx + 1}
                        </span>

                        <label
                          className="btn btn-glass btn-sm"
                          style={{
                            padding: '0.25rem 0.6rem',
                            fontSize: '0.68rem',
                            cursor: 'pointer',
                            width: '100%',
                            textAlign: 'center'
                          }}
                        >
                          <span>{subImg ? 'Change' : 'Choose'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'sub', idx)}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* =========================================================
                  PRODUCT SHOWCASE VIDEO / REEL
                  ========================================================= */}
              <div style={{
                background: '#0d0d16',
                border: '1px solid var(--border-gold)',
                borderRadius: 'var(--radius-md)',
                padding: '1.4rem'
              }}>
                {/* Hidden File Input for Phone / PC Video Picking */}
                <input
                  type="file"
                  ref={videoInputRef}
                  accept="video/mp4,video/webm,video/quicktime,video/*"
                  onChange={handleVideoUpload}
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', paddingBottom: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Video size={18} color="var(--gold-400)" />
                    <div>
                      <h4 style={{ fontSize: '0.98rem', color: '#fff', fontWeight: 800, margin: 0 }}>
                        Product Showcase Video (Reel / Video Clip)
                      </h4>
                      <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0 }}>
                        Upload video directly from Phone / Laptop, or paste YouTube / Shorts link
                      </p>
                    </div>
                  </div>
                  {form.videoUrl && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, videoUrl: '' })}
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#f87171',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ✕ Video Hatao
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Primary Option: Direct File Upload Button */}
                  <div style={{
                    background: 'rgba(212, 175, 55, 0.05)',
                    border: '1px dashed var(--gold-400)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.6rem'
                  }}>
                    <button
                      type="button"
                      disabled={isUploadingVideo}
                      onClick={() => videoInputRef.current?.click()}
                      className="btn btn-gold btn-sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontWeight: 800,
                        padding: '0.6rem 1.4rem',
                        cursor: isUploadingVideo ? 'wait' : 'pointer'
                      }}
                    >
                      <Film size={17} />
                      {isUploadingVideo ? '⏳ Video Upload Ho Rahi Hai...' : '📁 Phone / Computer se Video Chunein (MP4 / MOV)'}
                    </button>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      Supports: MP4, WebM, MOV (iPhone Camera Video), Reel Clips up to 100MB
                    </p>
                  </div>

                  {/* Secondary Option: Link or YouTube / Shorts */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-300)', marginBottom: '0.35rem', fontWeight: 700 }}>
                      Ya Video URL / YouTube / Shorts Link Paste Karein:
                    </label>
                    <input
                      type="text"
                      value={form.videoUrl || ''}
                      onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                      className="input-luxury"
                      placeholder="e.g. https://www.youtube.com/shorts/... or https://assets.mixkit.co/...mp4"
                      style={{ fontWeight: 600 }}
                    />
                  </div>

                  {/* Quick Preset Video Samples for testing */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Quick Samples:</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, videoUrl: 'https://assets.mixkit.co/videos/41221/41221-720.mp4' })}
                      style={{
                        fontSize: '0.7rem',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: form.videoUrl === 'https://assets.mixkit.co/videos/41221/41221-720.mp4' ? 'rgba(212, 175, 55, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                        color: form.videoUrl === 'https://assets.mixkit.co/videos/41221/41221-720.mp4' ? 'var(--gold-300)' : 'var(--text-muted)',
                        border: form.videoUrl === 'https://assets.mixkit.co/videos/41221/41221-720.mp4' ? '1px solid var(--gold-400)' : '1px solid var(--border-subtle)',
                        cursor: 'pointer'
                      }}
                    >
                      👑 Gold Ring Showcase Reel
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, videoUrl: 'https://assets.mixkit.co/videos/41222/41222-720.mp4' })}
                      style={{
                        fontSize: '0.7rem',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: form.videoUrl === 'https://assets.mixkit.co/videos/41222/41222-720.mp4' ? 'rgba(212, 175, 55, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                        color: form.videoUrl === 'https://assets.mixkit.co/videos/41222/41222-720.mp4' ? 'var(--gold-300)' : 'var(--text-muted)',
                        border: form.videoUrl === 'https://assets.mixkit.co/videos/41222/41222-720.mp4' ? '1px solid var(--gold-400)' : '1px solid var(--border-subtle)',
                        cursor: 'pointer'
                      }}
                    >
                      ✨ Bridal Necklace Showcase Reel
                    </button>
                  </div>

                  {/* Video Player Live Preview */}
                  {form.videoUrl && (
                    <div style={{
                      marginTop: '0.6rem',
                      background: '#07070a',
                      borderRadius: '8px',
                      padding: '0.8rem',
                      border: '1px solid var(--border-gold)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PlayCircle size={14} /> Video Preview Active
                      </div>
                      {(form.videoUrl.includes('youtube.com') || form.videoUrl.includes('youtu.be')) ? (
                        <iframe
                          src={(() => {
                            const url = form.videoUrl;
                            let id = '';
                            if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1]?.split('?')[0];
                            else if (url.includes('youtube.com/shorts/')) id = url.split('youtube.com/shorts/')[1]?.split('?')[0];
                            else if (url.includes('youtube.com/watch')) {
                              const m = url.match(/[?&]v=([^&]+)/);
                              id = m ? m[1] : '';
                            } else if (url.includes('youtube.com/embed/')) return url;
                            return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=0&controls=1&rel=0` : url;
                          })()}
                          title="Video Preview"
                          style={{
                            width: '100%',
                            maxHeight: '220px',
                            minHeight: '190px',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          src={form.videoUrl}
                          controls
                          autoPlay
                          loop
                          muted
                          playsInline
                          style={{
                            maxHeight: '210px',
                            maxWidth: '100%',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem', marginTop: '0.5rem' }}>
                <button 
                  type="button" 
                  className="btn btn-glass btn-sm"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-gold btn-sm" style={{ fontWeight: 800 }}>
                  {editingId ? 'Update & Save Product' : 'Save & Publish to Vault'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
